import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

const PCCameraStream = forwardRef(({
    active,
    backendUrl,
    uploadSnapshotUrl,
    uploadRecordingUrl,
    modelId,
    evalId,
    token,
    onSnapshotsChange,
    hideSnapshotGallery = false,
    compactControls = false,
}, ref) => {
    const videoRef = useRef(null);
    const overlayCanvasRef = useRef(null);
    const hiddenCanvasRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordingChunksRef = useRef([]);
    const frameIntervalRef = useRef(null);

    const [cameraOn, setCameraOn] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [boxes, setBoxes] = useState([]);
    const [snapshots, setSnapshots] = useState([]);
    const [recordings, setRecordings] = useState([]);
    const [error, setError] = useState("");

    const getPreferredFacingMode = () => {
        const isMobile =
            /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
                navigator.userAgent
            );

        return isMobile ? "environment" : "user";
    };

    const startCamera = async () => {
        try {
            if (mediaStreamRef.current) return;

            setError("");

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: getPreferredFacingMode(),
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                },
                audio: true,
            });

            mediaStreamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }

            setCameraOn(true);
        } catch (err) {
            console.error("Camera access failed:", err);
            setError("Unable to access camera/microphone. Please allow permissions.");
        }
    };

    const stopCamera = () => {
        if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state !== "inactive"
        ) {
            mediaRecorderRef.current.stop();
        }

        if (frameIntervalRef.current) {
            clearInterval(frameIntervalRef.current);
            frameIntervalRef.current = null;
        }

        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach((track) => track.stop());
            mediaStreamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setCameraOn(false);
        setIsRecording(false);
        setBoxes([]);
        clearOverlay();
    };

    const clearOverlay = () => {
        const canvas = overlayCanvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const syncCanvasSize = () => {
        const video = videoRef.current;
        const canvas = overlayCanvasRef.current;
        const hiddenCanvas = hiddenCanvasRef.current;
        if (!video || !canvas || !hiddenCanvas) return;

        const width = video.videoWidth || 640;
        const height = video.videoHeight || 480;

        canvas.width = width;
        canvas.height = height;
        hiddenCanvas.width = width;
        hiddenCanvas.height = height;
    };

    const drawBoxes = (detections = []) => {
        const canvas = overlayCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.lineWidth = 2;
        ctx.font = "16px Arial";

        detections.forEach((det) => {
            const { x, y, width, height, label, score } = det;

            ctx.strokeStyle = "#00ff00";
            ctx.fillStyle = "#00ff00";
            ctx.strokeRect(x, y, width, height);

            const text = `${label || "obj"} ${score ? (score * 100).toFixed(1) : ""}%`;
            const textY = y > 20 ? y - 8 : y + 18;
            ctx.fillText(text, x, textY);
        });
    };

    const captureFrameBlob = async () => {
        const video = videoRef.current;
        const canvas = hiddenCanvasRef.current;
        if (!video || !canvas) return null;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        return new Promise((resolve) => {
            canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.8);
        });
    };

    const sendFrameToBackend = async () => {
        try {
            const blob = await captureFrameBlob();
            if (!blob) return;

            const formData = new FormData();
            formData.append("frame", blob, "frame.jpg");
            formData.append("model_id", modelId || "");
            formData.append("eval_id", evalId || "");

            const response = await fetch(backendUrl, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Frame prediction request failed");
            }

            const result = await response.json();

            const detections = result?.boxes || [];
            setBoxes(detections);
            drawBoxes(detections);
        } catch (err) {
            console.error("Prediction error:", err);
        }
    };

    const startPredictionLoop = () => {
        if (frameIntervalRef.current) clearInterval(frameIntervalRef.current);

        frameIntervalRef.current = setInterval(() => {
            if (videoRef.current?.readyState >= 2) {
                // sendFrameToBackend();
            }
        }, 500);
    };

    const takeSnapshot = async () => {
        try {
            const blob = await captureFrameBlob();
            if (!blob) return;

            const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
            const fileName = `snapshot_${id}.jpg`;
            const previewUrl = URL.createObjectURL(blob);

            const snapshotItem = {
                id,
                blob,
                fileName,
                previewUrl,
                createdAt: new Date().toISOString(),
            };

            setSnapshots((prev) => [...prev, snapshotItem]);

            const formData = new FormData();
            formData.append("snapshot", blob, fileName);
            formData.append("model_id", modelId || "");
            formData.append("eval_id", evalId || "");
            formData.append("snapshot_id", id);
        } catch (err) {
            console.error("Snapshot upload failed:", err);
        }
    };

    const removeSnapshot = (id) => {
        setSnapshots((prev) => {
            const item = prev.find((s) => s.id === id);
            if (item?.previewUrl) {
                URL.revokeObjectURL(item.previewUrl);
            }
            return prev.filter((s) => s.id !== id);
        });
    };

    const startRecording = () => {
        if (!mediaStreamRef.current) return;

        recordingChunksRef.current = [];

        const recorder = new MediaRecorder(mediaStreamRef.current, {
            mimeType: "video/webm",
        });

        recorder.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
                recordingChunksRef.current.push(event.data);
            }
        };

        recorder.onstop = async () => {
            try {
                const blob = new Blob(recordingChunksRef.current, {
                    type: "video/webm",
                });

                const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                const fileName = `recording_${id}.webm`;
                const previewUrl = URL.createObjectURL(blob);

                const recordingItem = {
                    id,
                    blob,
                    fileName,
                    previewUrl,
                    createdAt: new Date().toISOString(),
                };

                setRecordings((prev) => [...prev, recordingItem]);

                const formData = new FormData();
                formData.append("recording", blob, fileName);
                formData.append("model_id", modelId || "");
                formData.append("eval_id", evalId || "");
                formData.append("recording_id", id);

                await fetch(uploadRecordingUrl, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                });
            } catch (err) {
                console.error("Recording upload failed:", err);
            }
        };

        mediaRecorderRef.current = recorder;
        recorder.start(1000);
        setIsRecording(true);
    };

    const stopRecording = () => {
        if (
            mediaRecorderRef.current &&
            mediaRecorderRef.current.state !== "inactive"
        ) {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
    };

    const removeRecording = (id) => {
        setRecordings((prev) => {
            const item = prev.find((r) => r.id === id);
            if (item?.previewUrl) {
                URL.revokeObjectURL(item.previewUrl);
            }
            return prev.filter((r) => r.id !== id);
        });
    };

    const clearAllMedia = () => {
        setSnapshots((prev) => {
            prev.forEach((snapshot) => {
                if (snapshot.previewUrl) URL.revokeObjectURL(snapshot.previewUrl);
            });
            return [];
        });

        setRecordings((prev) => {
            prev.forEach((recording) => {
                if (recording.previewUrl) URL.revokeObjectURL(recording.previewUrl);
            });
            return [];
        });
    };

    useImperativeHandle(ref, () => ({
        removeSnapshot,
        clearAllMedia,
    }));

    useEffect(() => {
        if (active) {
            startCamera();
        } else {
            stopCamera();
        }

        return () => {
            stopCamera();
        };
    }, [active]);

    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, []);

    useEffect(() => {
        return () => {
            setSnapshots((prev) => {
                prev.forEach((snap) => {
                    if (snap.previewUrl) URL.revokeObjectURL(snap.previewUrl);
                });
                return [];
            });
        };
    }, []);

    useEffect(() => {
        if (!onSnapshotsChange) return;

        onSnapshotsChange(
            snapshots.map((snap) => ({
                id: snap.id,
                name: snap.fileName,
                url: snap.previewUrl,
                file: new File([snap.blob], snap.fileName, {
                    type: snap.blob?.type || "image/jpeg",
                    lastModified: Date.now(),
                }),
                type: "image",
                createdAt: snap.createdAt,
            }))
        );
    }, [onSnapshotsChange, snapshots]);

    return (
        <div className={`pc-camera-stream${compactControls ? " pc-camera-stream--compact" : ""}`}>
            {error && <div className="alert alert-danger">{error}</div>}

            <div className="pc-camera-stream__video-wrap">
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    playsInline
                    onLoadedMetadata={() => {
                        syncCanvasSize();
                        startPredictionLoop();
                    }}
                    className="pc-camera-stream__video"
                />

                <canvas
                    ref={overlayCanvasRef}
                    className="pc-camera-stream__overlay"
                />

                <canvas ref={hiddenCanvasRef} style={{ display: "none" }} />
            </div>

            <div className={`pc-camera-stream__actions${compactControls ? " pc-camera-stream__actions--compact" : ""}`}>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={takeSnapshot}
                    disabled={!cameraOn}
                >
                    Take Snapshot
                </button>

                {!isRecording ? (
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={startRecording}
                        disabled={!cameraOn}
                    >
                        Start Recording
                    </button>
                ) : (
                    <button
                        type="button"
                        className="btn btn-warning"
                        onClick={stopRecording}
                    >
                        Stop Recording
                    </button>
                )}
            </div>

            {!hideSnapshotGallery && (snapshots.length > 0 || recordings.length > 0) && (
                <div className="d-flex justify-content-end mt-3">
                    <button
                        type="button"
                        className="btn btn-link text-danger p-0"
                        onClick={clearAllMedia}
                    >
                        Clear All
                    </button>
                </div>
            )}

            {!hideSnapshotGallery && snapshots.length > 0 && (
                <div className="mt-2">
                    <h6>Snapshots</h6>
                    <div
                        className="d-flex flex-nowrap gap-3 overflow-auto pb-2"
                        style={{ width: "100%" }}
                    >
                        {snapshots.map((snap) => (
                            <div
                                key={snap.id}
                                style={{ width: 140, minWidth: 140, flex: "0 0 140px", position: "relative" }}
                            >
                                <button
                                    type="button"
                                    onClick={() => removeSnapshot(snap.id)}
                                    style={{
                                        position: "absolute",
                                        top: 6,
                                        right: 6,
                                        border: "none",
                                        borderRadius: "50%",
                                        width: 24,
                                        height: 24,
                                        background: "rgba(0,0,0,0.6)",
                                        color: "#fff",
                                        cursor: "pointer",
                                    }}
                                >
                                    ×
                                </button>

                                <img
                                    src={snap.previewUrl}
                                    alt={snap.fileName}
                                    style={{
                                        width: "100%",
                                        height: 90,
                                        objectFit: "cover",
                                        borderRadius: 8,
                                        border: "1px solid #ddd",
                                    }}
                                />
                                <div style={{ fontSize: 12, marginTop: 6, wordBreak: "break-word" }}>
                                    {snap.fileName}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!hideSnapshotGallery && recordings.length > 0 && (
                <div className="mt-3">
                    <h6>Recordings</h6>
                    <div
                        className="d-flex flex-nowrap gap-3 overflow-auto pb-2"
                        style={{ width: "100%" }}
                    >
                        {recordings.map((rec) => (
                            <div
                                key={rec.id}
                                style={{
                                    width: 190,
                                    minWidth: 190,
                                    flex: "0 0 190px",
                                    position: "relative",
                                    border: "1px solid #ddd",
                                    borderRadius: 8,
                                    padding: 8,
                                }}
                            >
                                <button
                                    type="button"
                                    onClick={() => removeRecording(rec.id)}
                                    style={{
                                        position: "absolute",
                                        top: 6,
                                        right: 6,
                                        border: "none",
                                        borderRadius: "50%",
                                        width: 24,
                                        height: 24,
                                        background: "rgba(0,0,0,0.6)",
                                        color: "#fff",
                                        cursor: "pointer",
                                        zIndex: 1,
                                    }}
                                >
                                    ×
                                </button>

                                <video
                                    controls
                                    src={rec.previewUrl}
                                    style={{
                                        width: "100%",
                                        height: 110,
                                        objectFit: "cover",
                                        borderRadius: 8,
                                        background: "#000",
                                    }}
                                />

                                <div
                                    style={{
                                        fontSize: 12,
                                        marginTop: 6,
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {rec.fileName}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
});

PCCameraStream.displayName = "PCCameraStream";

export default PCCameraStream;
